import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  BarChart3,
  Receipt,
  RefreshCw,
  Users,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  Target,
  TrendingUp,
  Wallet,
  PiggyBank
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Monyply! 🎉',
    description: 'Your personal finance tracker to manage expenses, income, investments, and budgets all in one place.',
    icon: Sparkles,
    color: 'from-emerald-500 to-teal-600',
    tips: [
      'Track daily expenses and income',
      'Set budgets and monitor spending',
      'Analyze your financial health',
      'Split expenses with friends'
    ]
  },
  {
    id: 'expenses',
    title: 'Track Your Expenses',
    description: 'Add and manage all your expenses. Categorize, tag, and set recurring expenses for bills.',
    icon: Receipt,
    color: 'from-red-500 to-orange-600',
    tips: [
      'Quick add from the "+" button',
      'Set categories and custom tags',
      'Multi-currency support',
      'Filter and search transactions'
    ]
  },
  {
    id: 'groups',
    title: 'Groups & Split Expenses',
    description: 'Share expenses with friends, family, or roommates. Track who owes what and settle up easily.',
    icon: Users,
    color: 'from-violet-500 to-purple-600',
    tips: [
      'Create groups for trips, roommates, etc.',
      'Split expenses equally or custom amounts',
      'Track balances automatically',
      'Settle debts with payment tracking'
    ]
  },
  {
    id: 'income-investments',
    title: 'Income & Investments',
    description: 'Track all your income sources and monitor your investment portfolio in one place.',
    icon: PiggyBank,
    color: 'from-green-500 to-emerald-600',
    tips: [
      'Record salary, freelance, rental income',
      'Track stocks, mutual funds, FDs, crypto',
      'Monitor investment growth',
      'View investment analytics & breakdown'
    ]
  },
  {
    id: 'recurring',
    title: 'Recurring Transactions',
    description: 'Automate tracking of regular expenses and income like rent, salary, or subscriptions.',
    icon: RefreshCw,
    color: 'from-cyan-500 to-blue-600',
    tips: [
      'Set frequency: Daily, Weekly, Monthly, Yearly',
      'Auto-generate future transactions',
      'Approve pending items when due',
      'Get notified when transactions are due'
    ]
  },
  {
    id: 'budgeting',
    title: 'Budgets & Goals',
    description: 'Set spending limits by category and track your progress towards financial goals.',
    icon: Target,
    color: 'from-amber-500 to-yellow-600',
    tips: [
      'Create budgets for each category',
      'Set monthly or custom periods',
      'Visual progress tracking',
      'Get alerts when exceeding budget'
    ]
  },
  {
    id: 'dashboard',
    title: 'Dashboard Overview',
    description: 'Your financial snapshot at a glance. View totals, trends, and insights about your money.',
    icon: BarChart3,
    color: 'from-blue-500 to-indigo-600',
    tips: [
      'See total income, expenses & investments',
      'View spending breakdown by category',
      'Track budget vs actual spending',
      'Filter by time periods (Weekly, Monthly, YTD)'
    ]
  },
  {
    id: 'complete',
    title: "You're All Set! 🚀",
    description: 'Start tracking your finances today. You can always access help from the menu.',
    icon: Wallet,
    color: 'from-emerald-500 to-green-600',
    tips: [
      'Access Help & Guide from user menu',
      'Use Quick Links for fast navigation',
      'Check notifications for pending items',
      'Explore all features at your own pace'
    ]
  }
];

const OnboardingTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();
  
  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('monyply_onboarding_complete', 'true');
    setIsVisible(false);
    // Navigate to Quick Links page at the end of tour
    navigate('/quick-links');
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <Card className="w-full max-w-lg bg-white shadow-2xl border-0 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
            style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>
        
        {/* Header with icon */}
        <div className={`p-6 bg-gradient-to-r ${step.color} text-white relative overflow-hidden`}>
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
          </div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
                Step {currentStep + 1} of {TOUR_STEPS.length}
              </span>
              <button 
                onClick={handleSkip}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Skip tour"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Icon className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold">{step.title}</h2>
            </div>
          </div>
        </div>
        
        <CardContent className="p-6">
          <p className="text-gray-600 mb-6 text-base leading-relaxed">
            {step.description}
          </p>
          
          {/* Tips list */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Key Features
            </h4>
            <ul className="space-y-2">
              {step.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 text-xs font-medium mt-0.5">
                    {index + 1}
                  </span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Navigation buttons - Reorganized for mobile visibility */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Next/Get Started button on left side for mobile visibility */}
              <Button
                onClick={handleNext}
                className={`gap-1 bg-gradient-to-r ${step.color} hover:opacity-90`}
              >
                {isLastStep ? "Get Started" : "Next"}
                {!isLastStep && <ChevronRight className="w-4 h-4" />}
              </Button>
              {!isLastStep && (
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-gray-500 hidden sm:inline-flex"
                >
                  Skip Tour
                </Button>
              )}
            </div>
          </div>
          
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {TOUR_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? 'w-6 bg-emerald-500' 
                    : index < currentStep 
                      ? 'bg-emerald-300' 
                      : 'bg-gray-200'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
